import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { CONTAINER, CTA_UTILS, EYEBROW, EYEBROW_MARK, H2, LEAD } from '../classes';
import { CountFigure, useCountProgress } from '../count-up';
import { cx, FORM_ID, IMG, m, useReducedMotion } from '../shared';

interface Cover {
  slug: string;
  name: string;
  width: number;
  height: number;
}

const COVERS: Cover[] = [
  { slug: 'valorant', name: 'Valorant', width: 256, height: 341 },
  { slug: 'cs2', name: 'Counter-Strike 2', width: 256, height: 341 },
  { slug: 'lol', name: 'League of Legends', width: 256, height: 341 },
  { slug: 'fortnite', name: 'Fortnite', width: 256, height: 341 },
  { slug: 'apex', name: 'Apex Legends', width: 256, height: 341 },
  { slug: 'dota2', name: 'Dota 2', width: 256, height: 341 },
  { slug: 'pubg', name: 'PUBG', width: 256, height: 341 },
  { slug: 'tarkov', name: 'Escape From Tarkov', width: 256, height: 341 },
  { slug: 'overwatch2', name: 'Overwatch 2', width: 256, height: 341 },
  { slug: 'r6', name: 'Rainbow Six Siege', width: 248, height: 391 },
  { slug: 'wow', name: 'World of Warcraft', width: 256, height: 341 },
  { slug: 'ffxiv', name: 'Final Fantasy XIV', width: 256, height: 341 },
  { slug: 'ragnarok', name: 'Ragnarok Online', width: 256, height: 341 },
  { slug: 'tibia', name: 'Tibia', width: 248, height: 391 },
  { slug: 'albion', name: 'Albion Online', width: 250, height: 392 },
  { slug: 'lineage2', name: 'Lineage II', width: 256, height: 341 },
  { slug: 'warface', name: 'Warface', width: 248, height: 391 },
  { slug: 'starcraft', name: 'StarCraft', width: 256, height: 341 },
];
// lane B runs the same shelf nine titles apart
const LANE_B = [...COVERS.slice(9), ...COVERS.slice(0, 9)];

function Track({ covers, lane, copy, label }: { covers: Cover[]; lane: 'a' | 'b'; copy: number; label?: string }) {
  const decorative = copy > 0 || lane === 'b';
  return (
    <ul className={`flex flex-none ${lane === 'a' ? 'gap-[var(--gap-a)] pe-[var(--gap-a)]' : 'gap-[var(--gap-b)] pe-[var(--gap-b)]'}`} aria-label={copy === 0 && lane === 'a' ? label : undefined} aria-hidden={copy > 0 ? 'true' : undefined}>
      {covers.map((cover) => (
        <li key={cover.slug} className={cx('flex-none overflow-hidden rounded-xl border border-[#2C3B31] bg-[#0D130F] shadow-[var(--shadow-sm)]', lane === 'b' && 'opacity-80 brightness-[.94] saturate-[.9]')}>
          <picture>
            <source type='image/avif' srcSet={`${IMG}/capa-${cover.slug}.avif`} />
            <img className={`block aspect-[256/341] w-auto object-cover object-bottom ${lane === 'a' ? 'h-[var(--h-a)]' : 'h-[var(--h-b)]'}`} src={`${IMG}/capa-${cover.slug}.webp`} alt={decorative ? '' : cover.name} width={cover.width} height={cover.height} loading='lazy' decoding='async' />
          </picture>
        </li>
      ))}
    </ul>
  );
}

export function Games({ onTrialClick }: { onTrialClick: (event: MouseEvent<HTMLAnchorElement>) => void }) {
  const { t } = useTranslation('pages/trial-matrix');
  const { locale } = useRouter();
  const reduced = useReducedMotion();
  const marqueeRef = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(true);
  const [ready, setReady] = useState(false);
  const [countRef, progress] = useCountProgress<HTMLParagraphElement>(1600);

  // no script / reduced motion: the shelves stand still; otherwise they loop, paused off screen
  useEffect(() => {
    setReady(true);
    const marquee = marqueeRef.current;
    if (!marquee || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => setOnScreen(!!entries[0]?.isIntersecting), { rootMargin: '100px 0px' });
    observer.observe(marquee);
    return () => observer.disconnect();
  }, []);

  const lanes: Array<{ lane: 'a' | 'b'; covers: Cover[] }> = [
    { lane: 'a', covers: COVERS },
    { lane: 'b', covers: LANE_B },
  ];

  return (
    <section className='relative scroll-mt-6 pb-[clamp(2.5rem,1.5rem+3vw,4.5rem)] pt-[clamp(3rem,1.5rem+6vw,6rem)]' id='jogos' aria-labelledby='jogos-t'>
      <div className={CONTAINER}>
        <div className='mb-[clamp(1.75rem,1rem+2.4vw,3rem)] grid items-end gap-6 min-[960px]:grid-cols-[minmax(0,1fr)_auto] min-[960px]:gap-10' data-reveal=''>
          <div className='min-[960px]:max-w-[44rem]'>
            <p className={EYEBROW}>
              <span className={EYEBROW_MARK} aria-hidden='true'>
                &gt;
              </span>{' '}
              {t('lp.games.eyebrow')}
            </p>
            <h2 className={H2} id='jogos-t'>
              <Trans t={t} i18nKey='lp.games.title' components={{ em: <em /> }} />
            </h2>
            <p className={LEAD}>{t('lp.games.lead')}</p>
          </div>
          <p ref={countRef} className='flex flex-wrap items-baseline gap-x-3 gap-y-2'>
            <strong className='whitespace-nowrap text-[min(clamp(2.5rem,1.6rem+3.4vw,4.25rem),19vw)] font-bold tabular-nums leading-[.9] tracking-[-.045em] text-[#93FF18] [text-shadow:0_0_34px_rgb(147_255_24/.4)] forced-colors:text-[LinkText]'>
              <CountFigure progress={progress} locale={locale} grouped className='tabular-nums' valueClassName='justify-self-end'>
                3000
              </CountFigure>
              +
            </strong>
            <span className='font-mono text-[.6875rem] uppercase leading-normal tracking-[.14em] text-[#9AA3B5]'>
              <Trans t={t} i18nKey='lp.games.countLabel' components={{ br: <br /> }} />
            </span>
          </p>
        </div>
      </div>

      <div ref={marqueeRef} className={m(cx('marquee', ready && !reduced && 'is-ready', !onScreen && 'is-off'))} id='capas'>
        {lanes.map(({ lane, covers }) => (
          <div key={lane} className={m(`marquee__row marquee__row--${lane}`)} aria-hidden={lane === 'b' ? 'true' : undefined}>
            {[0, 1, 2].map((copy) => (
              <Track key={copy} covers={covers} lane={lane} copy={copy} label={t('lp.games.listLabel')} />
            ))}
          </div>
        ))}
      </div>

      <div className={CONTAINER}>
        <div className='flex flex-wrap items-center justify-start gap-4'>
          <a className={`${m('btn-primary cta')} ${CTA_UTILS}`} href={`#${FORM_ID}`} onClick={onTrialClick}>
            {t('lp.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}
